<template>
  <div>
    <the-nav-bar :title="$t('notice.title')" class="nav-bar" />
    <div class="body-wrapper" v-if="isLoaded">
      <md-scroll-view
        ref="scrollView"
        :scrolling-x="false"
        auto-reflow
        @end-reached="onReachEnd"
      >
        <div class="scroll-wrapper">
          <div
            class="alert-wrapper"
            v-for="(item, index) in noticeList"
            :key="index"
          >
            <div class="alert-title row">
              <div class="type row">
                <img :src="item.assetIconUrl" :alt="item.assetName" />
                <span>
                  {{
                    item.type === 'buy' ? $t('common.buy') : $t('common.sell')
                  }}
                  {{ item.assetSymbol }}
                </span>
              </div>
              <div class="status">
                <md-switch
                  v-model="item.status"
                  @change="statusChange(item.id, item.status)"
                ></md-switch>
              </div>
            </div>
            <div class="alert-content">
              <div class="content-detail row">
                <div class="title">{{ $t('notice.fiatCurrency') }}</div>
                <div class="content">
                  {{ `${item.legalCurrencySymbol}(${item.legalCurrencyName})` }}
                </div>
              </div>
              <div class="content-detail row">
                <div class="title">{{ $t('notice.minPercentage') }}</div>
                <div class="content">
                  {{ getPriceText(item.currType, item.minPercentage) }}
                </div>
              </div>
              <div class="content-detail row">
                <div class="title">{{ $t('notice.maxPercentage') }}</div>
                <div class="content">
                  {{ getPriceText(item.currType, item.maxPercentage) }}
                </div>
              </div>
              <div class="content-detail row">
                <div class="title">{{ $t('notice.frequency') }}</div>
                <div
                  class="content"
                  v-if="navLang === 'zh-cn' && item.alertCount !== 1"
                >
                  {{ `每个广告 ${item.alertCount} 次，间隔 ${item.alertGap} 小时` }}
                </div>
                <div
                  class="content"
                  v-else-if="navLang === 'zh-cn' && item.alertCount === 1"
                >
                  {{ `每个广告 ${item.alertCount} 次` }}
                </div>
                <div
                  class="content"
                  v-else-if="navLang === 'en' && item.alertCount !== 1"
                >
                  {{
                    `${item.alertCount} times per ad, ${item.alertGap}-hour interval`
                  }}
                </div>
                <div
                  class="content"
                  v-else-if="navLang === 'en' && item.alertCount === 1"
                >
                  {{ `${item.alertCount} times per ad` }}
                </div>
              </div>
            </div>
            <div class="alert-operate row">
              <md-button
                round
                class="operate delete"
                @click="alertDelete(item.id)"
              >
                {{ $t('notice.delete') }}
              </md-button>
              <md-button
                round
                type="primary"
                class="operate"
                @click="alertEdit(item)"
              >
                {{ $t('notice.edit') }}
              </md-button>
            </div>
          </div>
        </div>
        <md-scroll-view-more
          slot="more"
          class="scroll-more"
          :is-finished="isFinished"
          :loading-text="$t('common.loading')"
          :finished-text="$t('common.noMore')"
        ></md-scroll-view-more>
      </md-scroll-view>
      <div class="bottom">
        <div class="button-wrapper row">
          <md-button class="add" round type="primary" @click="addAlert">
            <svg class="icon">
              <use xlink:href="#icon-plus" />
            </svg>
            <span>{{ $t('notice.add') }}</span>
          </md-button>
        </div>
        <div class="help">
          <p>
            1. {{ $t('notice.smartTrade') }}
            <!-- <span @click="goIntro">https://w3c.group/c/1589188815497213</span> -->
          </p>
          <p>2. {{ $t('orderDetail.assistance') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import getAdNotice from '@/api/get-ad-notice';
import putNoticeStatus from '@/api/put-notice-status';
import deleteAdNotice from '@/api/delete-ad-notice';
import TheNavBar from '@/components/the-nav-bar';
import { ScrollView, Button, Switch, Toast, ScrollViewMore } from 'mand-mobile';
export default {
  components: {
    TheNavBar,
    [ScrollView.name]: ScrollView,
    [Button.name]: Button,
    [Switch.name]: Switch,
    [ScrollViewMore.name]: ScrollViewMore,
  },
  data() {
    return {
      isLoaded: false,
      isFinished: false,
      currentPage: 1,
      sumPerPage: 10,
      noticeList: [],
    };
  },
  computed: mapState({
    userInfo: state => state.userInfo,
    navLang: state => state.navLang,
  }),
  mounted() {
    Toast.loading(this.$t('common.loading'));
    this.loadMore()
      .then(data => {
        Toast.hide();
        this.isLoaded = true;
        if (!data.links || !data.links.next) {
          this.isFinished = true;
        }
        this.$refs.scrollView && this.$refs.scrollView.finishLoadMore();
      })
      .catch(err => {
        Toast.hide();
        this.isLoaded = true;
        Toast.failed(`${this.$t('error.occur')}${err.message}`);
      });
  },
  methods: {
    loadMore() {
      return getAdNotice(this.currentPage, this.sumPerPage)
        .then(data => {
          const d = data.data;
          if (Array.isArray(d)) {
            d.forEach(item => {
              this.noticeList.push({
                id: item.id,
                type: item.type,
                assetIconUrl: item.asset.iconUrl,
                assetName: item.asset.name,
                assetSymbol: item.asset.symbol,
                status: !item.status,
                legalCurrencySymbol: item.legalCurrency.symbol,
                legalCurrencyName: item.legalCurrency.name,
                minPercentage: item.minPercentage,
                maxPercentage: item.maxPercentage,
                alertCount: item.alertCount,
                alertGap: item.alertGap,
                currType: item.currType,
              });
            });
          }
          this.currentPage += 1;
          this.$refs.scrollView && this.$refs.scrollView.reflowScroller();
          return Promise.resolve(data);
        })
        .catch(err => {
          Toast.failed(`${this.$t('error.occur')}${err.message}`);
        });
    },
    statusChange(id, status) {
      const statusParam = status ? 0 : 1;
      putNoticeStatus(id, statusParam)
        .then(() => {
          Toast.succeed(this.$t('notice.changeSuccess'));
        })
        .catch(err => {
          Toast.failed(`${this.$t('error.occur')}${err.message}`);
        });
    },
    alertDelete(id) {
      Toast.loading(this.$t('common.loading'));
      deleteAdNotice(id)
        .then(() => {
          Toast.hide();
          Toast.succeed(this.$t('notice.removeSuccess'));
          const index = this.noticeList.findIndex(item => {
            return item.id === id;
          });
          this.noticeList.splice(index, 1);
        })
        .catch(err => {
          Toast.hide();
          Toast.failed(`${this.$t('error.occur')}${err.message}`);
        });
    },
    alertEdit(item) {
      this.$store.dispatch('updateNoticeStore', { id: item.id });
      this.$router.push({
        path: '/mine/notice/add',
        query: {
          id: item.id,
        },
      });
    },
    addAlert() {
      this.$router.push('/mine/notice/add');
    },
    goIntro() {
      window.open('https://w3c.group/c/1589188815497213', '_blank');
    },
    onReachEnd() {
      if (this.isFinished) return;
      this.loadMore()
        .then(data => {
          if (!data.links || !data.links.next) {
            this.isFinished = true;
          }
          this.$refs.scrollView && this.$refs.scrollView.finishLoadMore();
        })
        .catch(err => {
          Toast.failed(`${this.$t('error.occur')}${err.message}`);
        });
    },
    getPriceText(type, price) {
      let str = '';
      if (Number(price) < 0) {
        str += this.$t('notice.below');
      } else {
        str += this.$t('notice.above');
      }
      if (type === 'exinone') {
        str += ` ${this.$t('customize.exinone')} ${Math.abs(
          Number(price),
        )} CNY`;
      } else {
        str += ` CoinMarketCap ${Math.abs(Number(price))}%`;
      }
      return str;
    },
  },
};
</script>

<style lang="stylus" scoped>
.body-wrapper {
  position: absolute;
  absolute-pos(11.73vw);
  background: #efefef;

  .scroll-wrapper {
    width: 100%;

    .alert-wrapper {
      margin-top: 2.13vw;
      background: #fff;

      .alert-title {
        height: 16vw;
        padding: 0 4.8vw;
        justify-content: space-between;
        align-items: center;
        border-bottom: 0.27vw solid #EAF1F3;

        .type {
          font-size: 3.73vw;
          font-weight: bold;
          align-items: center;

          img {
            width: 6.4vw;
            height: 6.4vw;
            margin-right: 2.13vw;
          }
        }

        .status {
          .md-switch {
            width: 11.73vw;
            height: 2.67vw;

            &:before {
              width: 11.73vw;
              height: 2.67vw;
            }

            &:after {
              width: 5.87vw;
              height: 5.87vw;
              top: -1.7vw;
              left: -1vw;
              box-shadow: 0px 0vw 1.86667vw 0px #bcc7cb;
            }

            &.active:after {
              transform: translateX(7.26667vw);
            }
          }
        }
      }

      .alert-content {
        padding: 4.8vw;
        border-bottom: 0.27vw solid #EAF1F3;

        .content-detail {
          color: #84969C;
          height: 8.53vw;
          align-items: center;
          justify-content: space-between;

          .content {
            font-weight: bold;
          }
        }
      }

      .alert-operate {
        height: 19.2vw;
        margin: 0 4.8vw;
        justify-content: flex-end;
        align-items: center;

        .operate {
          width: 21.33vw;
          height: 9.6vw;

          &.delete {
            margin-right: 9.6vw;
            color: #1b6fdc;

            /deep/&::after {
              border-color: #1b6fdc;
            }
          }
        }
      }
    }
  }

  .scroll-more {
    padding-bottom: 60vw;
    // margin-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
    // margin-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */
  }

  .bottom {
    background: #FFF;
    width: 100%;
    padding-top: 2.13vw;
    position: fixed;
    bottom: 0vw;
    z-index: 101;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    box-shadow: 0vw -1.07vw 2.13vw 0vw rgba(58, 86, 131, 0.07);

    .button-wrapper {
      justify-content: center;

      .add {
        width: 42.67vw;
        height: 9.6vw;

        .icon {
          width: 3.73vw;
          height: 3.73vw;
          margin-right: 2.13vw;
          margin-top: 1vw;
        }
      }
    }

    .help {
      margin: 2.13vw 4.8vw 4.13vw;

      p {
        margin-top: 1.07vw;
        font-size: 3.2vw;
        color: #84969C;
        text-align: left;

        span {
          color: #1b6fdc;
        }
      }
    }
  }
}
</style>
